import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { createSection, type SectionLocale } from '../../api/sectionsService';

const SUPPORTED_LOCALES: Record<string, string> = {
  en: 'English',
  ar: 'Arabic',
};

interface AddSectionPopupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendorId: string;
}

const AddSectionPopup: React.FC<AddSectionPopupProps> = ({
  open,
  onClose,
  onSuccess,
  vendorId,
}) => {
  const [sectionName, setSectionName] = useState('');
  const [displayOrder, setDisplayOrder] = useState<string>('');
  const [currentLang, setCurrentLang] = useState<string>('en');
  const [locale, setLocale] = useState<SectionLocale>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; displayOrder?: string }>({});

  const handleClose = () => {
    if (!isSubmitting) {
      setSectionName('');
      setDisplayOrder('');
      setLocale({});
      setCurrentLang('en');
      setErrors({});
      onClose();
    }
  };

  const handleLanguageChange = useCallback(
    (newLang: string) => {
      const trimmedName = sectionName.trim();
      const nextLocale: SectionLocale = {
        ...locale,
        [currentLang]: trimmedName ? { name: trimmedName } : undefined,
      };
      const nextLocaleRecord = nextLocale as Record<string, { name?: string } | undefined>;
      if (!nextLocaleRecord[currentLang]?.name) delete nextLocaleRecord[currentLang];
      setLocale(nextLocale);
      setCurrentLang(newLang);
      setSectionName(nextLocaleRecord[newLang]?.name ?? '');
    },
    [currentLang, locale, sectionName]
  );

  const validateForm = (): boolean => {
    const newErrors: { name?: string; displayOrder?: string } = {};

    if (!sectionName.trim()) {
      newErrors.name = 'Section name is required';
    }

    if (!displayOrder.trim()) {
      newErrors.displayOrder = 'Display order is required';
    } else {
      const orderNum = parseInt(displayOrder, 10);
      if (isNaN(orderNum) || orderNum < 0) {
        newErrors.displayOrder = 'Display order must be a valid positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const finalLocale: SectionLocale = {
        ...locale,
        [currentLang]: sectionName.trim() ? { name: sectionName.trim() } : undefined,
      };
      const defaultName =
        (finalLocale.en?.name ?? (currentLang === 'en' ? sectionName.trim() : '')) || sectionName.trim();

      const response = await createSection(vendorId, {
        name: defaultName,
        displayOrder: parseInt(displayOrder, 10),
        ...(Object.keys(finalLocale).length > 0 && { locale: finalLocale }),
      });

      if (response.success) {
        toast.success('Section created successfully!');
        setSectionName('');
        setDisplayOrder('');
        setLocale({});
        setCurrentLang('en');
        setErrors({});
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Failed to create section');
      }
    } catch (error: any) {
      console.error('Error creating section:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to create section. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            fontSize: '20px',
            color: '#181818',
          }}
        >
          Add Section
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {/* Language selector */}
            <FormControl fullWidth size="small">
              <Select
                value={currentLang}
                onChange={(e) => handleLanguageChange(e.target.value)}
                displayEmpty
                disabled={isSubmitting}
                sx={{
                  borderRadius: '12px',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                }}
              >
                {Object.entries(SUPPORTED_LOCALES).map(([code, label]) => (
                  <MenuItem key={code} value={code}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Section Name Field */}
            <TextField
              fullWidth
              label="Section Name"
              value={sectionName}
              onChange={(e) => {
                setSectionName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              error={!!errors.name}
              helperText={errors.name}
              required
              disabled={isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                },
              }}
            />

            {/* Display Order Field */}
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={(e) => {
                setDisplayOrder(e.target.value);
                if (errors.displayOrder) {
                  setErrors({ ...errors, displayOrder: undefined });
                }
              }}
              error={!!errors.displayOrder}
              helperText={errors.displayOrder || 'Enter a number to determine the order of this section'}
              required
              disabled={isSubmitting}
              inputProps={{
                min: 0,
                step: 1,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              textTransform: 'none',
              color: '#181818',
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              textTransform: 'none',
              backgroundColor: '#67314f',
              '&:hover': {
                backgroundColor: '#5a2a42',
              },
              minWidth: '120px',
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create Section'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddSectionPopup;

